import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, RefreshCw, PlayCircle, Lightbulb, X as XIcon, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ListeningRuleDialog,
  type ListeningRuleEditable,
} from './ListeningRuleDialog';
import { FenceDialog, type FenceEditable } from './FenceDialog';
import { IntensityGaugeBadge, gaugeLabel } from './IntensityGauge';
import { getRssSourceByUrl } from '@/lib/rss-sources';
import { AIRuleBuilder } from './AIRuleBuilder';

// ── AI Suggestion type ────────────────────────────────────────────────────────
interface AISuggestion {
  rule_type: 'filter' | 'listening'
  name: string
  description: string
  keywords: string[]
  confidence: 'high' | 'medium'
}

type RuleType = 'filter' | 'listening' | 'blocking' | 'monitor' | 'allow';

interface RuleRow {
  id: string;
  user_id: string;
  name: string;
  rule_type: RuleType | null;
  keywords: string[] | null;
  blocked_users: string[] | null;
  blocked_hashtags: string[] | null;
  blocked_accounts: string[] | null;
  fence_new_accounts: boolean | null;
  new_account_threshold_days: number | null;
  fence_low_followers: boolean | null;
  min_follower_threshold: number | null;
  platforms: string[] | null;
  rss_feeds: string[] | null;
  intensity_gauge: number | null;
  highlight_style: { pin?: boolean; badge?: boolean; email_digest?: boolean } | null;
  is_active: boolean;
  created_at: string;
}

function isFence(rule: RuleRow): boolean {
  return rule.rule_type === 'filter' || rule.rule_type === 'blocking';
}

function isListening(rule: RuleRow): boolean {
  return !isFence(rule); // listening / monitor / allow / null all behave as listening
}

function rssSourceLabels(rss_feeds: string[] | null): string {
  if (!rss_feeds || !rss_feeds.length) return '';
  return rss_feeds
    .slice(0, 3)
    .map((url) => getRssSourceByUrl(url)?.name ?? new URL(url).hostname.replace(/^www\./, ''))
    .join(', ') + (rss_feeds.length > 3 ? `, +${rss_feeds.length - 3}` : '');
}

export const UserRulesManager: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightTag = searchParams.get('highlight');
  const ruleRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [rules, setRules] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const [listeningOpen, setListeningOpen] = useState(false);
  const [editingListening, setEditingListening] = useState<ListeningRuleEditable | undefined>();
  const [fenceOpen, setFenceOpen] = useState(false);
  const [editingFence, setEditingFence] = useState<FenceEditable | undefined>();

  // AI smart suggestions
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  useEffect(() => {
    if (user) {
      fetchRules();
      fetchSuggestions();
    }
  }, [user]);

  // Scroll to and highlight a rule when navigated from Analytics tag click
  useEffect(() => {
    if (!highlightTag || loading || rules.length === 0) return;
    const t = setTimeout(() => {
      const el = ruleRefs.current.get(highlightTag);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(t);
  }, [highlightTag, loading, rules.length]);

  const fetchRules = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('filtering_rules')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });
    if (error) {
      toast.error('Failed to load rules');
    } else {
      setRules((data ?? []) as RuleRow[]);
    }
    setLoading(false);
  };

  const { listeningRules, fenceRules } = useMemo(() => {
    const l: RuleRow[] = [];
    const f: RuleRow[] = [];
    for (const r of rules) {
      if (isFence(r)) f.push(r);
      else l.push(r);
    }
    return { listeningRules: l, fenceRules: f };
  }, [rules]);

  // Fetch AI suggestions based on manually-hidden posts (no rule tags = user manually hid them)
  const fetchSuggestions = async () => {
    if (!user || suggestionsDismissed) return;
    setSuggestionsLoading(true);
    try {
      const { data: hiddenItems } = await supabase
        .from('social_feed_items')
        .select('title, body')
        .eq('user_id', user.id)
        .eq('is_filtered', true)
        .is('rule_tags', null)
        .order('fetched_at', { ascending: false })
        .limit(50);

      if (!hiddenItems || hiddenItems.length < 5) return; // not enough signal

      const snippets = hiddenItems
        .map((i) => (i.title ?? i.body ?? '').slice(0, 120))
        .filter(Boolean);

      const { data } = await supabase.functions.invoke('ai-assistant', {
        body: { action: 'suggest_rules', hidden_snippets: snippets },
      });

      if (data?.success && Array.isArray(data.suggestions) && data.suggestions.length) {
        setSuggestions(data.suggestions as AISuggestion[]);
      }
    } catch {
      // silent — suggestions are non-critical
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const openListeningCreate = (prefill?: ListeningRuleEditable) => {
    setEditingListening(prefill);
    setListeningOpen(true);
  };

  const openListeningEdit = (rule: RuleRow) => {
    setEditingListening({
      id: rule.id,
      name: rule.name,
      keywords: rule.keywords ?? [],
      platforms: rule.platforms ?? [],
      rss_feeds: rule.rss_feeds ?? [],
      intensity_gauge: rule.intensity_gauge ?? 3,
      highlight_style: rule.highlight_style ?? {},
    });
    setListeningOpen(true);
  };

  const openFenceCreate = (prefill?: FenceEditable) => {
    setEditingFence(prefill);
    setFenceOpen(true);
  };

  const openFenceEdit = (rule: RuleRow) => {
    setEditingFence({
      id: rule.id,
      name: rule.name,
      keywords: rule.keywords ?? [],
      blocked_users: rule.blocked_users ?? [],
      blocked_hashtags: rule.blocked_hashtags ?? [],
      blocked_accounts: rule.blocked_accounts ?? [],
      fence_new_accounts: rule.fence_new_accounts ?? false,
      new_account_threshold_days: rule.new_account_threshold_days ?? 30,
      fence_low_followers: rule.fence_low_followers ?? false,
      min_follower_threshold: rule.min_follower_threshold ?? null,
      platforms: rule.platforms ?? [],
      rss_feeds: rule.rss_feeds ?? [],
      intensity_gauge: rule.intensity_gauge ?? 3,
    });
    setFenceOpen(true);
  };

  const handleDelete = async (rule: RuleRow) => {
    if (!confirm(`Delete "${rule.name}"? Items it tagged or filtered will need to be recomputed.`)) {
      return;
    }
    const { error } = await supabase.from('filtering_rules').delete().eq('id', rule.id);
    if (error) {
      toast.error('Failed to delete rule');
      return;
    }
    toast.success('Rule deleted');
    fetchRules();
  };

  const handleRecompute = async () => {
    if (!user) return;
    setRecomputing(true);
    try {
      const { data, error } = await supabase.functions.invoke('apply-rules-to-feed', {
        body: { force: true },
      });
      if (error) {
        toast.error(`Couldn't apply rules: ${error.message}`);
        return;
      }
      const processed = data?.processed ?? 0;
      const filtered = data?.filtered ?? 0;
      const tagged = data?.tagged ?? 0;
      const pinned = data?.pinned ?? 0;
      const demoted = data?.demoted ?? 0;
      toast.success(`Applied rules to ${processed} earlier posts`, {
        description: `${tagged} tagged · ${pinned} pinned · ${demoted} demoted · ${filtered} hidden`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't apply rules";
      toast.error(msg);
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading rules…</span>
      </div>
    );
  }

  const hasAnyRules = rules.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Content rules</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Listening rules surface what you care about. Fences hide what you don't. The intensity
            gauge controls how strongly each rule acts.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRecompute}
          disabled={recomputing || !hasAnyRules}
          title={!hasAnyRules ? 'Create a rule first' : 'Run your current rules across the posts already in your feed'}
        >
          <PlayCircle className={`w-4 h-4 mr-2 ${recomputing ? 'animate-pulse' : ''}`} />
          {recomputing ? 'Applying…' : 'Apply rules to earlier posts'}
        </Button>
      </div>

      {/* ── AI Rule Builder ──────────────────────────────────────────── */}
      <AIRuleBuilder
        onCreateListening={(v) => openListeningCreate(v)}
        onCreateFence={(v) => openFenceCreate(v)}
      />

      {/* ── AI Smart Suggestions ─────────────────────────────────────── */}
      {!suggestionsDismissed && suggestions.length > 0 && (
        <div className="rounded-lg border border-amber-200/60 bg-amber-50/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600/70" />
              <span className="text-sm font-medium">AI spotted patterns in your feed</span>
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5">AI</Badge>
            </div>
            <button
              onClick={() => setSuggestionsDismissed(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Based on posts you've hidden, here are rules that might help automate that.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {suggestions.map((s, i) => (
              <div key={i} className="rounded-md border bg-background p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold">{s.name}</span>
                  <Badge
                    variant={s.rule_type === 'filter' ? 'destructive' : 'default'}
                    className="text-[10px] py-0 px-1.5"
                  >
                    {s.rule_type === 'filter' ? 'Fence' : 'Listen'}
                  </Badge>
                  {s.confidence === 'high' && (
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-green-700 border-green-200">
                      High confidence
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">{s.description}</p>
                {s.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {s.keywords.slice(0, 5).map((kw) => (
                      <Badge key={kw} variant="secondary" className="text-[10px] py-0 px-1">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[11px] w-full"
                  onClick={() => {
                    if (s.rule_type === 'filter') {
                      openFenceCreate({
                        name: s.name,
                        keywords: s.keywords ?? [],
                        blocked_users: [], blocked_hashtags: [], blocked_accounts: [],
                        fence_new_accounts: false, new_account_threshold_days: 30,
                        fence_low_followers: false, min_follower_threshold: null,
                        platforms: [], rss_feeds: [], intensity_gauge: 3,
                      });
                    } else {
                      openListeningCreate({
                        name: s.name,
                        keywords: s.keywords ?? [],
                        platforms: [], rss_feeds: [],
                        intensity_gauge: 3, highlight_style: { badge: true },
                      });
                    }
                  }}
                >
                  Create this rule
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-lg">Listening Rules</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Surface or highlight matching content.
              </p>
            </div>
            <Button size="sm" onClick={() => openListeningCreate()}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            {listeningRules.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No listening rules yet. Add one to surface specific topics in your feed.
              </p>
            ) : (
              <div className="space-y-3">
                {listeningRules.map((rule) => (
                  <div
                    key={rule.id}
                    ref={(el) => {
                      if (el) ruleRefs.current.set(rule.name, el);
                      else ruleRefs.current.delete(rule.name);
                    }}
                    className={`rounded-md transition-all duration-700 ${highlightTag === rule.name ? 'ring-2 ring-[#1A2E4A] ring-offset-2' : ''}`}
                  >
                    <RuleCard
                      rule={rule}
                      mode="listening"
                      onEdit={() => openListeningEdit(rule)}
                      onDelete={() => handleDelete(rule)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-lg">Fences</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Hide or de-emphasize content you don't want to see.
              </p>
            </div>
            <Button size="sm" onClick={() => openFenceCreate()}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            {fenceRules.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No fences yet. Add one to filter spam, doomscrolling, or specific accounts.
              </p>
            ) : (
              <div className="space-y-3">
                {fenceRules.map((rule) => (
                  <div
                    key={rule.id}
                    ref={(el) => {
                      if (el) ruleRefs.current.set(rule.name, el);
                      else ruleRefs.current.delete(rule.name);
                    }}
                    className={`rounded-md transition-all duration-700 ${highlightTag === rule.name ? 'ring-2 ring-[#1A2E4A] ring-offset-2' : ''}`}
                  >
                    <RuleCard
                      rule={rule}
                      mode="fence"
                      onEdit={() => openFenceEdit(rule)}
                      onDelete={() => handleDelete(rule)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ListeningRuleDialog
        open={listeningOpen}
        onOpenChange={setListeningOpen}
        initialValue={editingListening}
        onSaved={fetchRules}
      />
      <FenceDialog
        open={fenceOpen}
        onOpenChange={setFenceOpen}
        initialValue={editingFence}
        onSaved={fetchRules}
      />
    </div>
  );
};

interface RuleCardProps {
  rule: RuleRow;
  mode: 'listening' | 'fence';
  onEdit: () => void;
  onDelete: () => void;
}

const RuleCard: React.FC<RuleCardProps> = ({ rule, mode, onEdit, onDelete }) => {
  const gauge = rule.intensity_gauge ?? 3;
  const off = gauge === 0 || !rule.is_active;
  return (
    <div
      className={`border border-border/60 rounded-md p-3 ${
        off ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-medium truncate">{rule.name}</span>
            <IntensityGaugeBadge mode={mode} value={gauge} />
            {off && (
              <Badge variant="secondary" className="text-[10px]">
                Off
              </Badge>
            )}
          </div>

          {rule.keywords && rule.keywords.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Keywords: </span>
              {rule.keywords.slice(0, 6).join(', ')}
              {rule.keywords.length > 6 && ` +${rule.keywords.length - 6}`}
            </div>
          )}

          {rule.blocked_users && rule.blocked_users.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Accounts: </span>
              {rule.blocked_users.slice(0, 4).join(', ')}
              {rule.blocked_users.length > 4 && ` +${rule.blocked_users.length - 4}`}
            </div>
          )}

          {rule.blocked_hashtags && rule.blocked_hashtags.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Hashtags: </span>
              {rule.blocked_hashtags.slice(0, 4).join(' ')}
              {rule.blocked_hashtags.length > 4 && ` +${rule.blocked_hashtags.length - 4}`}
            </div>
          )}

          {rule.platforms && rule.platforms.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Platforms: </span>
              {rule.platforms.join(', ')}
            </div>
          )}

          {rule.rss_feeds && rule.rss_feeds.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">RSS: </span>
              {rssSourceLabels(rule.rss_feeds)}
            </div>
          )}

          <div className="text-[11px] text-muted-foreground mt-1">
            {gaugeLabel(mode, gauge)} · created {new Date(rule.created_at).toLocaleDateString()}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
